import type { Money, Odds, SignedMoney } from '@urbanrats/contracts';
import { formatAtomic, parseLocaleDecimal, type MoneyError, type Result } from '@urbanrats/domain';

export function separators(locale: string): { group: string; decimal: string } {
  const parts = new Intl.NumberFormat(locale).formatToParts(12345.6);
  return {
    group: parts.find((p) => p.type === 'group')?.value ?? ',',
    decimal: parts.find((p) => p.type === 'decimal')?.value ?? '.',
  };
}

/** Localises an exact fixed-point string ("-1234.50") without converting to float. */
export function localiseDecimal(fixed: string, locale: string): string {
  const { group, decimal } = separators(locale);
  const neg = fixed.startsWith('-');
  const [whole = '0', frac] = (neg ? fixed.slice(1) : fixed).split('.');
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, group);
  return `${neg ? '−' : ''}${grouped}${frac !== undefined ? decimal + frac : ''}`;
}

/** Full-precision amount. Display rounding never hides the real stake. */
export function fmtMoney(m: Money | SignedMoney, locale: string): string {
  return `${localiseDecimal(formatAtomic(BigInt(m.amountAtomic), m.scale), locale)} ${m.currency}`;
}

export function fmtAtomic(atomic: bigint, scale: number, currency: string, locale: string): string {
  return `${localiseDecimal(formatAtomic(atomic, scale), locale)} ${currency}`;
}

/** Odds as a total-return multiplier with 2 decimals (floored, display only). */
export function fmtOdds(o: Odds, locale: string): string {
  const hundredths = (BigInt(o.numerator) * 100n) / BigInt(o.denominator);
  return `×${localiseDecimal(formatAtomic(hundredths, 2), locale)}`;
}

/** Parses a typed/pasted amount in the player's locale to atomic units (exact; errors instead of rounding). */
export function parseAmountInput(input: string, locale: string, scale: number): Result<bigint, MoneyError> {
  return parseLocaleDecimal(input, separators(locale), scale);
}

/** Exact short label: trailing fractional zeros removed (never rounded). "0.00050000" → "0.0005". */
export function fmtShort(atomic: bigint, scale: number, locale: string): string {
  const fixed = formatAtomic(atomic, scale);
  const trimmed = fixed.includes('.') ? fixed.replace(/0+$/, '').replace(/\.$/, '') : fixed;
  return localiseDecimal(trimmed, locale);
}

/** Readable local date-time for an ISO instant (display only; the UTC value stays in data/evidence). */
export function fmtDateTime(iso: string, locale: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString(locale, { dateStyle: 'medium', timeStyle: 'short' });
}
